import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import apiService, {
  type CompanyChatKind,
  type CompanyChatProfile,
  type CompanyChatSearchPreference,
} from "../services/api";

const NOTES_MAX = 2000;

const KIND_OPTIONS: Array<{ value: CompanyChatKind | ""; label: string }> = [
  { value: "", label: "Sin definir" },
  { value: "medio", label: "Medio" },
  { value: "medio_tv", label: "Medio - TV" },
  { value: "agencia", label: "Agencia" },
  { value: "marca", label: "Marca" },
];

const SEARCH_OPTIONS: Array<{
  value: CompanyChatSearchPreference;
  label: string;
  help: string;
}> = [
  {
    value: "auto",
    label: "Automática (agencia, Explorador primero)",
    help: "Una marca, programa o canal nombrado se busca en el Explorador. Mi contenido solo si dice 'mi/nuestro' o lo elige con @.",
  },
  {
    value: "mi_contenido_first",
    label: "Mi contenido primero",
    help: "Si nombra uno de sus canales o programas, responde primero con Mi contenido y luego con el mercado.",
  },
];

const NOTES_PLACEHOLDER =
  "Ej: Es un canal de TV. Le interesan los livestreams de Mega, CHV y Canal 13. Su podcast en Mi contenido es 'La Mañana'.";

const CompanyChatProfilePanel: React.FC<{ companyId: string }> = ({
  companyId,
}) => {
  const queryClient = useQueryClient();
  const queryKey = ["company-chat-profile", companyId];
  const profileQuery = useQuery({
    queryKey,
    queryFn: () => apiService.getCompanyChatProfile(companyId),
  });

  const [kind, setKind] = useState<CompanyChatKind | "">("");
  const [searchPreference, setSearchPreference] =
    useState<CompanyChatSearchPreference>("auto");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const p = profileQuery.data;
    if (!p) return;
    setKind(p.kind ?? "");
    setSearchPreference(p.searchPreference);
    setNotes(p.notes ?? "");
  }, [profileQuery.data]);

  const save = useMutation({
    mutationFn: (profile: CompanyChatProfile) =>
      apiService.updateCompanyChatProfile(companyId, profile),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      setSaved(true);
    },
  });

  const searchHelp = SEARCH_OPTIONS.find((o) => o.value === searchPreference)?.help;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Contexto del chat
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Se inyecta en el chat de todos los usuarios de esta empresa, junto con
        sus canales asignados y los livestreams habilitados.
      </Typography>

      {profileQuery.isLoading ? (
        <CircularProgress size={20} />
      ) : profileQuery.isError ? (
        <Alert severity="error">No pudimos cargar el contexto del chat.</Alert>
      ) : (
        <Stack spacing={2}>
          {saved && (
            <Alert severity="success" onClose={() => setSaved(false)}>
              Contexto del chat guardado.
            </Alert>
          )}
          {save.error ? (
            <Alert severity="error">
              {(save.error as any)?.response?.data?.message ??
                "No pudimos guardar el contexto del chat."}
            </Alert>
          ) : null}
          <FormControl size="small" fullWidth>
            <InputLabel id="chat-profile-kind">Tipo</InputLabel>
            <Select
              labelId="chat-profile-kind"
              label="Tipo"
              value={kind}
              onChange={(e) => setKind(e.target.value as CompanyChatKind | "")}
            >
              {KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value || "none"} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Le dice al chat con quién habla; no cambia dónde busca.
            </Typography>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="chat-profile-search">Preferencia de búsqueda</InputLabel>
            <Select
              labelId="chat-profile-search"
              label="Preferencia de búsqueda"
              value={searchPreference}
              onChange={(e) =>
                setSearchPreference(e.target.value as CompanyChatSearchPreference)
              }
            >
              {SEARCH_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {searchHelp && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {searchHelp}
              </Typography>
            )}
          </FormControl>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Notas para el chat"
            placeholder={NOTES_PLACEHOLDER}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, NOTES_MAX))}
            helperText={`${notes.length}/${NOTES_MAX}. Lo que no está en los datos: qué canales o livestreams le interesan, qué marca es el cliente, etc.`}
          />
          <Box>
            <Button
              variant="contained"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({
                  kind: kind || null,
                  searchPreference,
                  notes: notes.trim() || null,
                })
              }
            >
              {save.isPending ? "Guardando..." : "Guardar contexto"}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default CompanyChatProfilePanel;
